import React, { useState } from "react";
import {
  Tournament,
  SingleEliminationTournament,
  ClassicLadderTournament,
  CourtConfiguration,
  PlayerConfiguration,
} from "./types/tournament";
import { League } from "./types/league";
import {
  EventManagerState,
  StoredEvent,
  TournamentUnion,
} from "./types/eventManager";
import { generateId } from "./utils/tournamentLogic";
import { createSingleEliminationTournament } from "./utils/bracketLogic";
import { createClassicLadderTournament } from "./utils/classicLadderLogic";
import {
  createRoundRobinLeague,
  createDivisionLeague,
  createLadderLeague,
  createFlexLeague,
  createHybridLeague,
  createKingOfCourtLeague,
} from "./utils/leagueLogic";
import {
  loadEventManagerState,
  addEvent,
  updateEvent,
  setActiveEvent,
  getActiveEvent,
} from "./utils/eventManager";
import HomePage from "./components/HomePage";
import TournamentSetup from "./components/TournamentSetup";
import TournamentDashboard from "./components/TournamentDashboard";
import BracketDashboard from "./components/BracketDashboard";
import ClassicLadderDashboard from "./components/ClassicLadderDashboard";
import LeagueSelection from "./components/LeagueSelection";
import RoundRobinDashboard from "./components/RoundRobinDashboard";
import FlexLeagueDashboard from "./components/FlexLeagueDashboard";
import DivisionLeagueDashboard from "./components/DivisionLeagueDashboard";
import GenericLeagueDashboard from "./components/GenericLeagueDashboard";
import EventManager from "./components/EventManager";
import "./App.css";

function App() {
  const [eventManagerState, setEventManagerState] = useState<EventManagerState>(
    () => loadEventManagerState()
  );
  const [currentView, setCurrentView] = useState<
    "home" | "tournament-setup" | "league-selection" | "event-manager"
  >("home");

  // Get current active event
  const activeEvent = getActiveEvent(eventManagerState);
  const currentTournament =
    activeEvent?.type === "tournament"
      ? (activeEvent.data as TournamentUnion)
      : null;
  const currentLeague =
    activeEvent?.type === "league" ? (activeEvent.data as League) : null;

  const createNewTournament = (
    name: string,
    teams: any[],
    courts: CourtConfiguration[],
    playerConfig: PlayerConfiguration,
    totalRounds: number,
    tournamentType: "speed_ladder" | "single_elimination" | "classic_ladder"
  ) => {
    let newTournament: TournamentUnion;

    if (tournamentType === "single_elimination") {
      newTournament = createSingleEliminationTournament(
        name,
        teams,
        totalRounds
      );
    } else if (tournamentType === "classic_ladder") {
      newTournament = createClassicLadderTournament(
        name,
        teams,
        courts.length,
        totalRounds
      );
    } else {
      newTournament = {
        id: generateId(),
        name,
        teams,
        rounds: [],
        currentRound: 1,
        totalRounds,
        courts,
        playerConfig,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        liveDashboardEnabled: true,
        qrCodeUrl: "",
        tournamentType: "speed_ladder",
      };
    }

    const newState = addEvent(eventManagerState, newTournament, "tournament");
    setEventManagerState(newState);
    setCurrentView("home");
  };

  const handleSelectTournament = () => {
    setCurrentView("tournament-setup");
  };

  const handleSelectLeague = () => {
    setCurrentView("league-selection");
  };

  const handleBackToHome = () => {
    setCurrentView("home");
  };

  const handleOpenEventManager = () => {
    setCurrentView("event-manager");
  };

  const handleSelectEvent = (event: StoredEvent | null) => {
    if (event) {
      const newState = setActiveEvent(eventManagerState, event.id);
      setEventManagerState(newState);
    } else {
      const newState = setActiveEvent(eventManagerState, null);
      setEventManagerState(newState);
    }
    setCurrentView("home");
  };

  const handleCreateNew = () => {
    setCurrentView("home");
  };

  const handleCreateLeague = (options: any) => {
    let newLeague: League;

    switch (options.leagueType) {
      case "round_robin":
        newLeague = createRoundRobinLeague(options);
        break;
      case "division":
        newLeague = createDivisionLeague(options);
        break;
      case "ladder":
        newLeague = createLadderLeague(options);
        break;
      case "flex":
        newLeague = createFlexLeague(options);
        break;
      case "hybrid":
        newLeague = createHybridLeague(options);
        break;
      case "king_of_court":
        newLeague = createKingOfCourtLeague(options);
        break;
      default:
        console.error("Unknown league type:", options.leagueType);
        return;
    }

    const newState = addEvent(eventManagerState, newLeague, "league");
    setEventManagerState(newState);
    setCurrentView("home");
  };

  const handleUpdateTournament = (updatedTournament: TournamentUnion) => {
    if (activeEvent) {
      const newState = updateEvent(
        eventManagerState,
        activeEvent.id,
        updatedTournament
      );
      setEventManagerState(newState);
    }
  };

  const handleUpdateLeague = (updatedLeague: League) => {
    if (activeEvent) {
      const newState = updateEvent(
        eventManagerState,
        activeEvent.id,
        updatedLeague
      );
      setEventManagerState(newState);
    }
  };

  const handleResetEvent = () => {
    const newState = setActiveEvent(eventManagerState, null);
    setEventManagerState(newState);
    setCurrentView("home");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Show header only for tournaments and leagues, not for home page or event manager */}
      {(currentTournament || currentLeague) && currentView === "home" && (
        <header className="bg-primary-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center">
                <h1 className="text-3xl font-bold">🏓 Pickleball Manager</h1>
              </div>
              {currentTournament && (
                <div className="text-sm">
                  <p className="font-semibold">{currentTournament.name}</p>
                  <p>
                    Round {currentTournament.currentRound} of{" "}
                    {currentTournament.totalRounds}
                  </p>
                </div>
              )}
              {currentLeague && (
                <div className="text-sm">
                  <p className="font-semibold">{currentLeague.name}</p>
                  <p>League • {currentLeague.leagueType}</p>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Home Page */}
        {currentView === "home" && !currentTournament && !currentLeague && (
          <HomePage
            onSelectTournament={handleSelectTournament}
            onSelectLeague={handleSelectLeague}
            onOpenEventManager={handleOpenEventManager}
            hasExistingEvents={eventManagerState.events.length > 0}
          />
        )}

        {/* Event Manager */}
        {currentView === "event-manager" && (
          <EventManager
            eventManagerState={eventManagerState}
            setEventManagerState={setEventManagerState}
            onSelectEvent={handleSelectEvent}
            onCreateNew={handleCreateNew}
            onBack={handleBackToHome}
          />
        )}

        {/* Tournament Setup */}
        {currentView === "tournament-setup" && (
          <TournamentSetup onStart={createNewTournament} />
        )}

        {/* League Selection */}
        {currentView === "league-selection" && (
          <LeagueSelection
            onBack={handleBackToHome}
            onSelectLeague={handleCreateLeague}
          />
        )}

        {/* Active Tournament Dashboards */}
        {currentView === "home" &&
          currentTournament &&
          currentTournament.tournamentType === "single_elimination" && (
            <BracketDashboard
              tournament={currentTournament as SingleEliminationTournament}
              setTournament={
                handleUpdateTournament as (
                  tournament: SingleEliminationTournament
                ) => void
              }
              onReset={handleResetEvent}
            />
          )}

        {currentView === "home" &&
          currentTournament &&
          currentTournament.tournamentType === "classic_ladder" && (
            <ClassicLadderDashboard
              tournament={currentTournament as ClassicLadderTournament}
              setTournament={
                handleUpdateTournament as (
                  tournament: ClassicLadderTournament
                ) => void
              }
              onReset={handleResetEvent}
            />
          )}

        {currentView === "home" &&
          currentTournament &&
          currentTournament.tournamentType === "speed_ladder" && (
            <TournamentDashboard
              tournament={currentTournament as Tournament}
              setTournament={
                handleUpdateTournament as (tournament: Tournament) => void
              }
              onReset={handleResetEvent}
            />
          )}

        {/* Active League Dashboards */}
        {currentView === "home" &&
          currentLeague &&
          currentLeague.leagueType === "round_robin" && (
            <RoundRobinDashboard
              league={currentLeague as any}
              setLeague={handleUpdateLeague as (league: any) => void}
              onReset={handleResetEvent}
            />
          )}

        {currentView === "home" &&
          currentLeague &&
          currentLeague.leagueType === "flex" && (
            <FlexLeagueDashboard
              league={currentLeague as any}
              setLeague={handleUpdateLeague as (league: any) => void}
              onReset={handleResetEvent}
            />
          )}

        {currentView === "home" &&
          currentLeague &&
          currentLeague.leagueType === "division" && (
            <DivisionLeagueDashboard
              league={currentLeague as any}
              setLeague={handleUpdateLeague as (league: any) => void}
              onReset={handleResetEvent}
            />
          )}

        {currentView === "home" &&
          currentLeague &&
          ["ladder", "hybrid", "king_of_court"].includes(
            currentLeague.leagueType
          ) && (
            <GenericLeagueDashboard
              league={currentLeague as any}
              setLeague={handleUpdateLeague as (league: any) => void}
              onReset={handleResetEvent}
            />
          )}
      </main>
    </div>
  );
}

export default App;
